import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CheckCircle, ChevronsUpDown, Check, MapPin, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPostcodeBySuburb, getSuburbSuggestions, getNearestSuburbs } from "@/data/australianSuburbs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GetQuote() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openServiceSelect, setOpenServiceSelect] = useState(false);
  const [suburbSuggestions, setSuburbSuggestions] = useState([]);
  const [showSuburbSuggestions, setShowSuburbSuggestions] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    service_type: '',
    postcode: '',
    suburb: '',
    description: ''
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_name || !formData.email || !formData.phone || 
        !formData.service_type || !formData.postcode || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Quote request submitted successfully! We'll match you with tradies shortly.");
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Handle suburb input for autocomplete
    if (field === 'suburb') {
      if (value.length >= 2) {
        const suggestions = getSuburbSuggestions(value);
        setSuburbSuggestions(suggestions);
        setShowSuburbSuggestions(suggestions.length > 0);
      } else {
        setSuburbSuggestions([]);
        setShowSuburbSuggestions(false);
      }
    }
  };

  const handleSuburbSelect = (suburb, postcode, state) => {
    setFormData({ 
      ...formData, 
      suburb: suburb,
      postcode: postcode 
    });
    setShowSuburbSuggestions(false);
    setSuburbSuggestions([]);
  };

  const getServiceName = (slug) => {
    const service = services.find(s => s.slug === slug);
    return service ? service.name : "Select a service";
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const nearbySuburbs = await getNearestSuburbs(latitude, longitude);
          
          if (nearbySuburbs && nearbySuburbs.length > 0) {
            // Auto-fill with the first nearest suburb
            const nearest = nearbySuburbs[0];
            handleSuburbSelect(nearest.suburb, nearest.postcode, nearest.state);
            toast.success(`Location detected: ${nearest.suburb}`);
          } else {
            toast.info("Couldn't find nearby suburbs. Please enter manually.");
          }
        } catch (error) {
          console.error("Error finding suburbs:", error);
          toast.error("Failed to find nearby suburbs");
        } finally {
          setLoadingLocation(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Couldn't access your location. Please enter suburb manually.");
        setLoadingLocation(false);
      }
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Get a Free Quote | Local Tradies Online</title>
        <meta name="description" content="Get free quotes from trusted local tradies across Australia. Compare prices and hire verified professionals for your project." />
      </Helmet>      <Navbar />

      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold tracking-tight text-[#0f172a] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="quote-heading"
            >
              Get Free Quotes
            </h1>
            <p className="text-lg text-slate-600">
              Tell us about your project and we'll connect you with verified tradies in your area.
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-slate-700">100% Free Service</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-slate-700">Verified Tradies Only</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-slate-700">Fast Response</span>
            </div>
          </div>

          {/* Full Width Form - matching tradie profile exactly */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-2xl p-6" data-testid="quote-form">
            <div className="flex items-center space-x-2 mb-4">
              <div className="bg-orange-100 rounded-full p-2">
                <Send className="w-5 h-5 text-[#f97316]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0f172a]">Request a Quote</h3>
                <p className="text-sm text-slate-600">Fill in your details and we'll connect you</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_name" className="text-[#0f172a]">Your Name *</Label>
                  <Input
                    id="customer_name"
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    className="bg-white"
                    placeholder="John Smith"
                    data-testid="customer-name-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#0f172a]">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-white"
                    placeholder="04XX XXX XXX"
                    data-testid="phone-input"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#0f172a]">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="bg-white"
                  placeholder="john@example.com"
                  data-testid="email-input"
                  required
                />
              </div>

              <div>
                <Label htmlFor="service_type" className="text-[#0f172a]">What Service Do You Need? *</Label>
                <Popover open={openServiceSelect} onOpenChange={setOpenServiceSelect}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openServiceSelect}
                      className="w-full justify-between bg-white"
                      data-testid="service-type-select"
                    >
                      {formData.service_type ? getServiceName(formData.service_type) : "Select a service"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search services..." />
                      <CommandList>
                        <CommandEmpty>No service found.</CommandEmpty>
                        <CommandGroup>
                          {services.map((service) => (
                            <CommandItem
                              key={service.id}
                              value={service.name}
                              onSelect={() => {
                                handleChange('service_type', service.slug);
                                setOpenServiceSelect(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.service_type === service.slug ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {service.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="suburb" className="text-[#0f172a]">Suburb</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleUseMyLocation}
                      disabled={loadingLocation}
                      className="text-xs text-[#f97316] hover:text-[#ea580c] hover:bg-orange-100 h-auto py-1 px-2"
                    >
                      <MapPin className="w-3 h-3 mr-1" />
                      {loadingLocation ? 'Locating...' : 'Use My Location'}
                    </Button>
                  </div>
                  <Input
                    id="suburb"
                    type="text"
                    value={formData.suburb}
                    onChange={(e) => handleChange('suburb', e.target.value)}
                    onBlur={() => setTimeout(() => setShowSuburbSuggestions(false), 200)}
                    onFocus={() => formData.suburb.length >= 2 && suburbSuggestions.length > 0 && setShowSuburbSuggestions(true)}
                    className="bg-white"
                    placeholder="Start typing suburb..."
                    data-testid="suburb-input"
                  />
                  {showSuburbSuggestions && suburbSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {suburbSuggestions.map((item, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-orange-50 cursor-pointer text-sm"
                          onMouseDown={() => handleSuburbSelect(item.suburb, item.postcode, item.state)}
                        >
                          <div className="font-medium">{item.suburb}, {item.state}</div>
                          <div className="text-xs text-slate-500">Postcode: {item.postcode}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="postcode" className="text-[#0f172a]">Postcode *</Label>
                  <Input
                    id="postcode"
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => handleChange('postcode', e.target.value)}
                    className="bg-white"
                    placeholder="2000"
                    data-testid="postcode-input"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-[#0f172a]">What do you need help with?</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="bg-white"
                  placeholder="Briefly describe your project or the service you need..."
                  data-testid="description-textarea"
                  rows={3}
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-[#f97316] hover:bg-[#ea580c] text-white font-bold text-lg"
                disabled={loading}
                data-testid="submit-quote-btn"
              >
                {loading ? 'Submitting...' : 'Get Free Quote'}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Your details will be shared with local tradies to provide you with quotes
              </p>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}